import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { RanchCreatePlantingAreaDialog } from '../dialogs/planting-area-dialog';
import { ILotEdit } from '../../ranch-settings/modals/lot/interfaces';

@Component({
	moduleId: module.id,
	selector: 'ranch-create-planting-areas-step',
	templateUrl: 'step-planting-areas.html',
	styleUrls: ['../ranch-create.scss']
})

export class RanchCreatePlantingAreasStepComponent {
	@Input()
	public form: FormArray;

	@Input()
	public isSaving = false;

	@Input()
	public ranchCoordinates: string;

	@Input()
	public subscriptions$: Subject<boolean>;

	@Output()
	public onBack: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onCancel: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onSubmit: EventEmitter<void> = new EventEmitter<void>();

	public get plantingAreaControls(): AbstractControl[] {
		if (!this.form) {
			return [];
		}

		return this.form.controls;
	}

	constructor(
		private _dialog: MatDialog,
		private _fb: FormBuilder
	) { }

	public back(): void {
		this.onBack.emit();
	}

	public cancel(): void {
		this.onCancel.emit();
	}

	public deletePlantingArea(index: number, event?: MouseEvent): void {
		if (event) {
			event.stopPropagation();
		}

		this.form.removeAt(index);
		this.form.markAsDirty();
	}

	public finish(): void {
		this.form.markAllAsTouched();
		this.form.updateValueAndValidity();

		if (!this.form.valid) {
			return;
		}

		this.onSubmit.emit();
	}

	public openAddPlantingAreaDialog(): void {
		this._openPlantingAreaDialog((lot: ILotEdit) => {
			if (!lot) {
				return;
			}

			this.form.push(this._initializeForm(lot));
			this.form.markAsDirty();
		});
	}

	public openEditPlantingAreaDialog(index: number, event?: MouseEvent): void {
		let lot: ILotEdit;

		if (event) {
			event.stopPropagation();
		}

		lot = this.form.at(index).value;

		this._openPlantingAreaDialog((updatedLot: ILotEdit) => {
			if (!updatedLot) {
				return;
			}

			this.form.setControl(index, this._initializeForm(updatedLot));
			this.form.markAsDirty();
		}, lot);
	}

	private _initializeForm(lot: ILotEdit): FormGroup {
		return this._fb.group({
			Id: [lot.Id],
			Name: [lot.Name, Validators.required],
			Acres: [lot.Acres, Validators.required],
			Coordinates: [lot.Coordinates, Validators.required],
			ObstructionDepth: [lot.ObstructionDepth],
			SoilLayers: [lot.SoilLayers],
			SoilWebProperties: [lot.SoilWebProperties]
		});
	}

	private _openPlantingAreaDialog(onClose: (lot: ILotEdit) => void, lot?: ILotEdit): void {
		this._dialog.open(RanchCreatePlantingAreaDialog, {
			width: '880px',
			data: {
				lot: lot,
				ranchCoordinates: this.ranchCoordinates
			}
		}).afterClosed().pipe(takeUntil(this.subscriptions$)).subscribe(onClose);
	}
}
